import React, { useEffect, useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TagMultiSelect from '../../components/crm/TagMultiSelect';
import { updateCampaign } from '../../api/campaigns';

const fieldClass =
  'w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export default function CampaignSettingsPopover({ open, onClose, campaign, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagNames, setTagNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !campaign) return;
    setName(campaign.name ?? '');
    setDescription(campaign.description ?? '');
    setTagNames(
      Array.isArray(campaign.tags)
        ? campaign.tags.map((t) => (typeof t === 'object' && t?.name ? t.name : String(t)))
        : [],
    );
    setError(null);
  }, [open, campaign]);

  if (!open || !campaign) return null;

  const campaignId = campaign._id || campaign.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || saving || !campaignId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await updateCampaign(campaignId, {
        name: name.trim(),
        description: description.trim(),
        tags: tagNames,
      });
      onSaved?.(res?.data?.campaign);
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Fermer les paramètres"
        onClick={onClose}
      />
      <div
        className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card shadow-elevation-3"
        role="dialog"
        aria-labelledby="campaign-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Paramètres
            </p>
            <h2 id="campaign-settings-title" className="text-lg font-semibold text-foreground">
              Modifier la campagne
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Titre, description et tags. Produits, HubUP et emails se gèrent dans les onglets.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
            aria-label="Fermer"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Titre *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldClass}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className={`${fieldClass} resize-none`}
              placeholder="Objectif, notes…"
            />
          </div>
          <TagMultiSelect
            label="Tags d'audience"
            value={tagNames}
            onChange={setTagNames}
            placeholder="Rechercher ou créer…"
          />
          <p className="text-xs text-muted-foreground">
            Les tags inexistants sont créés à l&apos;enregistrement.
          </p>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Annuler
            </Button>
            <Button type="submit" iconName="Check" disabled={saving || !name.trim()}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
